<template>
  <div class="submission-list">
    <div class="toolbar">
      <el-select
        v-model="statusFilter"
        placeholder="全部状态"
        clearable
        style="width: 200px"
      >
        <el-option
          v-for="(s, k) in STATUS_MAP"
          :key="k"
          :label="s.text"
          :value="Number(k)"
        />
      </el-select>
      <el-button @click="fetchSubmissions" :icon="Refresh">刷新</el-button>
    </div>

    <div
      v-if="trackedId !== null"
      class="judge-tracker"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <el-alert
        :title="trackerTitle"
        :type="trackerType"
        show-icon
        :closable="false"
      >
        <div class="tracker-description">
          <span>{{ trackerDescription }}</span>
          <el-button v-if="trackError" size="small" @click="startTracking(trackedId)">重新获取</el-button>
        </div>
      </el-alert>
    </div>

    <el-skeleton :loading="loading" animated :count="6">
      <template v-if="!loading">
        <el-table
          v-if="submissionList.length > 0"
          :data="submissionList"
          :row-class-name="getRowClassName"
          stripe
          class="submission-table"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column label="题目" min-width="200">
            <template #default="{ row }">
              <router-link :to="`/problem/${row.questionId}`" class="problem-link">
                {{ questionTitles[row.questionId] ? questionTitles[row.questionId] + ' ' : '' }}#{{ row.questionId }}
              </router-link>
            </template>
          </el-table-column>
          <el-table-column prop="language" label="语言" width="100" align="center" />
          <el-table-column label="状态" width="180" align="center">
            <template #default="{ row }">
              <el-tag
                :type="getStatusType(row.status)"
                effect="light"
                size="small"
              >
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="判题信息" min-width="280">
            <template #default="{ row }">
              <span v-if="!row.judgeInfo" class="judge-empty">-</span>
              <div v-else class="judge-info-cell">
                <el-tag :type="getJudgeMsgType(parseJudgeInfo(row.judgeInfo).message)" size="small" effect="plain">
                  {{ parseJudgeInfo(row.judgeInfo).message }}
                </el-tag>
                <span class="judge-metrics">
                  <span v-if="parseJudgeInfo(row.judgeInfo).time != null" class="metric">
                    <el-icon><Timer /></el-icon> {{ parseJudgeInfo(row.judgeInfo).time }}ms
                  </span>
                  <span v-if="parseJudgeInfo(row.judgeInfo).memory != null" class="metric">
                    <el-icon><Monitor /></el-icon> {{ formatMemory(parseJudgeInfo(row.judgeInfo).memory) }}
                  </span>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="代码" width="90" align="center">
            <template #default="{ row }">
              <el-button size="small" @click="viewCode(row)">查看</el-button>
            </template>
          </el-table-column>
          <el-table-column label="提交时间" width="180" align="center">
            <template #default="{ row }">
              {{ formatTime(row.createTime) }}
            </template>
          </el-table-column>
        </el-table>

        <el-empty v-else description="暂无提交记录" />

        <el-pagination
          v-if="total > pageSize"
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next, total"
          background
          class="pagination"
          @current-change="fetchSubmissions"
        />
      </template>
    </el-skeleton>

    <el-alert
      v-if="error"
      type="error"
      :title="error"
      closable
      class="error-alert"
      @close="error = ''"
    />

    <el-dialog v-model="codeDialogVisible" title="提交代码" width="700px" top="5vh">
      <pre class="code-preview"><code>{{ codeDialogContent }}</code></pre>
      <template #footer>
        <el-button @click="codeDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElNotification } from 'element-plus'
import { Refresh, Timer, Monitor } from '@element-plus/icons-vue'
import { listQuestionSubmitByPage, getQuestionSubmitById } from '@/api/timutijiaoguanli'
import { getQuestionById } from '@/api/timuguanli'

const route = useRoute()

const questionTitles = ref({})

const STATUS_MAP = {
  0: { text: '等待中', type: 'info' },
  1: { text: '判题中', type: 'primary' },
  2: { text: '通过', type: 'success' },
  3: { text: '不通过', type: 'danger' },
  4: { text: '编译错误', type: 'warning' },
  5: { text: '超时', type: 'warning' },
  6: { text: '内存超限', type: 'warning' },
  7: { text: '运行错误', type: 'danger' },
  8: { text: '系统错误', type: 'danger' },
}

function getStatusText(status) {
  return STATUS_MAP[status]?.text ?? `未知(${status})`
}

function getStatusType(status) {
  return STATUS_MAP[status]?.type ?? 'info'
}

function isFinalStatus(status) {
  const value = Number(status)
  return value >= 2 && value <= 8
}

function parseJudgeInfo(str) {
  if (!str) return { message: '-', time: null, memory: null }
  try {
    const info = JSON.parse(str)
    return {
      message: info?.message || info?.msg || '-',
      time: info?.time ?? null,
      memory: info?.memory ?? null,
    }
  } catch {
    return { message: str, time: null, memory: null }
  }
}

function getJudgeMsgType(msg) {
  if (!msg || msg === '-') return 'info'
  if (msg === '通过' || msg === 'Accepted') return 'success'
  if (msg === '不通过' || msg === 'Wrong Answer') return 'danger'
  if (msg === '编译错误' || msg === 'Compile Error') return 'warning'
  if (msg === '超时' || msg === 'Time Limit Exceeded') return 'warning'
  if (msg === '内存超限' || msg === 'Memory Limit Exceeded') return 'warning'
  if (msg === '运行错误' || msg === 'Runtime Error') return 'danger'
  if (msg === '系统错误' || msg === 'System Error') return 'danger'
  return 'info'
}

function formatMemory(bytes) {
  if (bytes == null) return ''
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return bytes + ' B'
}

function formatTime(time) {
  if (!time) return '-'
  const d = new Date(time)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const submissionList = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(15)
const statusFilter = ref(null)
const loading = ref(false)
const error = ref('')
const codeDialogVisible = ref(false)
const codeDialogContent = ref('')
const trackedId = ref(null)
const trackedSubmission = ref(null)
const tracking = ref(false)
const trackError = ref('')
let pollTimer = null
let pollGeneration = 0

const trackerTitle = computed(() => {
  if (trackError.value) return '判题状态获取失败'
  if (!trackedSubmission.value) return '提交已受理'
  const status = Number(trackedSubmission.value.status)
  if (status === 0) return '提交成功，等待判题'
  if (status === 1) return '正在判题'
  return `判题完成：${getStatusText(status)}`
})

const trackerDescription = computed(() => {
  const id = `提交编号 #${trackedId.value ?? '-'}`
  if (trackError.value) return `${id}，${trackError.value}`
  if (!trackedSubmission.value) return `${id}，正在获取判题状态…`
  if (!isFinalStatus(trackedSubmission.value.status)) {
    return `${id}，状态会自动更新，无需手动刷新`
  }
  const info = parseJudgeInfo(trackedSubmission.value.judgeInfo)
  const metrics = [
    info.time != null ? `${info.time} ms` : '',
    info.memory != null ? formatMemory(info.memory) : '',
  ].filter(Boolean)
  return [id, info.message, ...metrics].filter(item => item && item !== '-').join(' · ')
})

const trackerType = computed(() => {
  if (trackError.value) return 'error'
  const status = Number(trackedSubmission.value?.status)
  if (!trackedSubmission.value || status < 2) return 'info'
  if (status === 2) return 'success'
  if ([4, 5, 6].includes(status)) return 'warning'
  return 'error'
})

async function fetchSubmissions() {
  loading.value = true
  error.value = ''
  try {
    const res = await listQuestionSubmitByPage({
      current: currentPage.value,
      pageSize: pageSize.value,
      status: statusFilter.value ?? undefined,
      sortField: 'createTime',
      sortOrder: 'descend',
    })
    if (res.code === 0) {
      submissionList.value = res.data?.records ?? []
      total.value = res.data?.total ?? 0
      fetchQuestionTitles()
    } else {
      throw new Error(res.message || '获取提交记录失败')
    }
  } catch (e) {
    error.value = e.message || '网络错误'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

async function fetchQuestionTitles() {
  const ids = [...new Set(submissionList.value.map(r => r.questionId).filter(Boolean))]
  const uncached = ids.filter(id => !questionTitles.value[id])
  if (uncached.length === 0) return
  const results = await Promise.allSettled(uncached.map(id => getQuestionById({ id })))
  const next = { ...questionTitles.value }
  results.forEach((r, i) => {
    if (r.status === 'fulfilled' && r.value.code === 0 && r.value.data) {
      next[uncached[i]] = r.value.data.title
    }
  })
  questionTitles.value = next
}

function getRowClassName({ row }) {
  return Number(row.id) === trackedId.value ? 'tracked-row' : ''
}

function applyTrackedSubmission(record) {
  const next = [...submissionList.value]
  const index = next.findIndex(item => Number(item.id) === Number(record.id))
  if (index >= 0) {
    next[index] = { ...next[index], ...record }
  } else {
    next.unshift(record)
    total.value += 1
  }
  submissionList.value = next
  fetchQuestionTitles()
}

function notifyJudgeFinished(record) {
  const status = Number(record.status)
  const type = status === 2 ? 'success' : [4, 5, 6].includes(status) ? 'warning' : 'error'
  ElNotification({
    title: status === 2 ? '判题通过' : '判题完成',
    message: `提交 #${record.id}：${getStatusText(status)}`,
    type,
    duration: 5000,
  })
}

async function pollSubmission(generation) {
  if (generation !== pollGeneration) return
  try {
    const res = await getQuestionSubmitById({ id: trackedId.value })
    if (generation !== pollGeneration) return
    if (res.code !== 0 || !res.data) {
      throw new Error(res.message || '未获取到判题结果')
    }
    trackedSubmission.value = res.data
    trackError.value = ''
    applyTrackedSubmission(res.data)
    if (isFinalStatus(res.data.status)) {
      tracking.value = false
      notifyJudgeFinished(res.data)
      return
    }
  } catch (e) {
    if (generation !== pollGeneration) return
    trackError.value = e.message || '网络错误，请重新获取'
    tracking.value = false
    return
  }
  if (generation === pollGeneration && tracking.value) {
    pollTimer = setTimeout(() => pollSubmission(generation), 1500)
  }
}

function startTracking(id) {
  const numericId = Number(id)
  if (!Number.isInteger(numericId) || numericId <= 0) return
  pollGeneration += 1
  if (pollTimer) clearTimeout(pollTimer)
  trackedId.value = numericId
  trackedSubmission.value = submissionList.value.find(item => Number(item.id) === numericId) || null
  tracking.value = true
  trackError.value = ''
  pollSubmission(pollGeneration)
}

function viewCode(row) {
  codeDialogContent.value = row.code || '// 无代码'
  codeDialogVisible.value = true
}

watch(statusFilter, () => {
  currentPage.value = 1
  fetchSubmissions()
})

onMounted(async () => {
  await fetchSubmissions()
  const submitId = Number(route.query.submitId)
  if (Number.isInteger(submitId) && submitId > 0) {
    startTracking(submitId)
  }
})

onBeforeUnmount(() => {
  pollGeneration += 1
  if (pollTimer) clearTimeout(pollTimer)
})
</script>

<style scoped>
.submission-list {
  padding: 28px;
  border-radius: 24px;
  border: 1px solid #d6d3d1;
  background: #ffffff;
  box-shadow: 0 18px 36px -18px rgba(120, 113, 108, 0.42), 0 5px 14px -8px rgba(120, 113, 108, 0.32);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #d6d3d1;
}

.judge-tracker {
  margin-bottom: 24px;
}

.judge-tracker :deep(.el-alert) {
  border: 1px solid #d6d3d1;
  box-shadow: 0 10px 20px -14px rgba(120, 113, 108, 0.4);
}

.tracker-description {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  color: var(--yoj-text-secondary);
  line-height: 1.7;
}

.submission-table {
  width: 100%;
}

.submission-table :deep(.tracked-row) td.el-table__cell {
  background: #fff7ed !important;
}

.problem-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--yoj-accent-strong);
  font-weight: 600;
  text-decoration: none;
}

.problem-link:hover {
  color: #9a3412;
  text-decoration: underline;
}

.judge-empty {
  color: var(--yoj-text-muted);
}

.judge-info-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.judge-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--yoj-text-secondary);
  font-family: Consolas, monospace;
  font-size: 12px;
}

.metric {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.pagination {
  justify-content: center;
  margin-top: 28px;
}

.error-alert {
  margin-top: 20px;
}

.code-preview {
  max-height: 500px;
  margin: 0;
  padding: 24px;
  overflow: auto;
  color: #44403c;
  border-radius: 16px;
  background: #fafaf9;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.7;
}

.code-preview code {
  white-space: pre;
}

@media (max-width: 640px) {
  .submission-list {
    padding: 20px 16px;
  }

  .toolbar > * {
    width: 100% !important;
  }
}
</style>
